import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft, FiPlus, FiEdit, FiTrash2, FiDownload,
  FiCopy, FiMoreVertical, FiSearch, FiFilter, FiCalendar,
  FiCheck, FiX, FiUpload, FiExternalLink, FiShield,
} from 'react-icons/fi';
import { BsDiagram3 as BsDiagram3Icon } from 'react-icons/bs';
import { ProjectAccessPanel } from './ProjectAccessPanel';
import { useAuth } from '../../context/AuthContext';
import { BsDiagram3 } from 'react-icons/bs';
import DashboardLayout from '../Dashboard/DashboardLayout';
import CreateArchitectureModal from '../canvas_drag_N_DROP/CreateArchitectureModal';
import { fabricTemplates } from '../../Config/fabric/fabricTemplates';
import ToastContainer from '../common/ToastContainer';
import ConfirmModal from '../common/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import * as projectsApi from '../../api/projects';
import * as architecturesApi from '../../api/architectures';
import { logActivity } from '../../api/activity';
import JSZip from 'jszip';
import { encryptArchitectureBlobs, decryptArchitectureBlobs } from '../../utils/encryption';

interface Architecture {
  id: number;
  name: string;
  description: string;
  type: string;
  deploymentType: string;
  nodesCount: number;
  lastModified: string;
  createdBy: string;
  status: 'draft' | 'validated' | 'deployed';
}

interface Project {
  id: number;
  name: string;
  description: string;
  provider: string;
  architectureCount: number;
  lastModified: string;
  createdBy: string;
  color: string;
}

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('architecture');
  const { toasts, removeToast, success, error: showError } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'architectures' | 'access'>('architectures');
  const [project, setProject] = useState<Project | null>(null);
  const [architectures, setArchitectures] = useState<Architecture[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedArchitectures, setSelectedArchitectures] = useState<Set<number>>(new Set());
  const [renameModal, setRenameModal] = useState<{ isOpen: boolean; archId: string | null; currentName: string }>({
    isOpen: false,
    archId: null,
    currentName: ''
  });
  const [newName, setNewName] = useState('');
  const [statusMenu, setStatusMenu] = useState<{isOpen: boolean; archId: number | null; currentStatus: Architecture['status']}>({
    isOpen: false,
    archId: null,
    currentStatus: 'draft'
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; archId: number | string | null; archName: string }>({
    isOpen: false,
    archId: null,
    archName: ''
  });

  useEffect(() => {
    loadProjectData();
    if (id) logActivity('project', id); // fire & forget
  }, [id]);

  // Fermer le menu quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeMenu) {
        setActiveMenu(null);
        setMenuPosition(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeMenu]);

  // Selection handlers
  const toggleArchitectureSelection = (archId: number) => {
    const newSelection = new Set(selectedArchitectures);
    if (newSelection.has(archId)) {
      newSelection.delete(archId);
    } else {
      newSelection.add(archId);
    }
    setSelectedArchitectures(newSelection);
  };

  const selectAllArchitectures = () => {
    setSelectedArchitectures(new Set(filteredArchitectures.map(a => a.id)));
  };

  const deselectAllArchitectures = () => {
    setSelectedArchitectures(new Set());
  };

  const loadProjectData = async () => {
    if (!id) return;

    try {
      // Load project from API
      console.log('📡 Chargement du projet depuis l\'API...', id);
      const apiProject = await projectsApi.getProject(id) as any;
      console.log('✅ Projet chargé:', apiProject);

      // Map to local format with provider-based color
      const provider = apiProject.provider || 'Azure';
      const providerLower = (apiProject.provider || 'azure').toLowerCase();
      const providerColors: Record<string, string> = {
        'azure': '#0078D4',
        'aws': '#FF9900',
        'gcp': '#34A853',
        'fabric': '#038387',
      };

      const mappedProject: Project = {
        id: apiProject.id,
        name: apiProject.name || 'Projet sans nom',
        description: apiProject.description || '',
        provider: provider,
        architectureCount: apiProject.architectures_count || 0,
        lastModified: apiProject.updated_at || apiProject.created_at,
        createdBy: apiProject.created_by_name || 'User',
        color: providerColors[providerLower] || '#0078D4'
      };

      setProject(mappedProject);

      // Load architectures from API
      console.log('📡 Chargement des architectures...');
      const apiArchitectures = await architecturesApi.getArchitecturesByProject(id);
      console.log('✅ Architectures chargées:', apiArchitectures.length);

      // Map to local format
      const mappedArchitectures: Architecture[] = apiArchitectures.map((a: any) => {
        // Parse diagram_data if it's a string
        let diagramData = a.diagram_data;
        if (typeof diagramData === 'string') {
          try {
            diagramData = JSON.parse(diagramData);
          } catch (e) {
            console.warn('Failed to parse diagram_data for architecture', a.id);
            diagramData = { nodes: [], edges: [] };
          }
        }

        return {
          id: a.id,
          name: a.name,
          description: a.description || '',
          type: a.type || 'Standard',
          deploymentType: 'Mono-cloud',
          nodesCount: diagramData?.nodes?.length || 0,
          lastModified: a.updated_at || a.created_at,
          createdBy: a.creator_name || 'User',
          status: a.status || 'draft'
        };
      });

      setArchitectures(mappedArchitectures);

    } catch (error: any) {
      console.error('❌ Erreur lors du chargement:', error);

      // Fallback to localStorage
      const projectsData = localStorage.getItem('projects.list');
      if (projectsData) {
        const projects: Project[] = JSON.parse(projectsData);
        const currentProject = projects.find(p => p.id === Number(id));
        if (currentProject) {
          setProject(currentProject);
        }
      }

      const archKey = `project.${id}.architectures`;
      const savedArchitectures = localStorage.getItem(archKey);
      if (savedArchitectures) {
        setArchitectures(JSON.parse(savedArchitectures));
      } else {
        setArchitectures([]);
      }
    }
  };

  const handleCreateArchitecture = () => {
    setShowCreateModal(true);
  };

  const handleSaveNewArchitecture = async (architectureData: any) => {
    if (!id || !project) return;

    try {
      console.log('📡 Création de l\'architecture via API...', architectureData);

      // If a Fabric template was selected, load its nodes/edges
      let initialDiagram: { nodes: any[]; edges: any[] } = { nodes: [], edges: [] };
      if (architectureData.diagramData) {
        // Direct diagram from wizard
        initialDiagram = architectureData.diagramData;
      } else if (architectureData.template && architectureData.template !== 'blank') {
        const fabricTpl = fabricTemplates.find(t => t.id === architectureData.template);
        if (fabricTpl) {
          initialDiagram = { nodes: fabricTpl.nodes, edges: fabricTpl.edges };
        }
      }

      // Create architecture via API
      const createdArchitecture = await architecturesApi.createArchitecture({
        projectId: id,
        name: architectureData.name,
        description: architectureData.description || '',
        provider: (project.provider.toLowerCase() as "azure" | "aws" | "gcp" | "fabric"),
        diagramData: initialDiagram
      });

      console.log('✅ Architecture créée:', createdArchitecture);

      // Reload architectures list
      await loadProjectData();
      setShowCreateModal(false);

      // Navigate to canvas to edit the new architecture
      navigate('/canvas', {
        state: {
          projectId: id,
          projectName: project.name,
          architectureId: createdArchitecture.id,
          architectureData: createdArchitecture
        }
      });

    } catch (error: any) {
      console.error('❌ Erreur lors de la création:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;

      // Afficher un toast d'erreur moderne
      showError(`Erreur lors de la création de l'architecture: ${errorMsg}`, 6000);

      // Fermer la modal et rester sur la page liste
      setShowCreateModal(false);

      // Recharger la liste pour être sûr que les données sont à jour
      await loadProjectData();
    }
  };

  const handleOpenArchitecture = (archId: number) => {
    navigate('/canvas', { state: { projectId: id, architectureId: archId } });
  };

  const handleDeleteArchitecture = async (archId: number | string) => {
    const arch = architectures.find(a => a.id === archId);
    if (!arch) return;

    // Ouvrir la modale de confirmation
    setDeleteConfirmModal({
      isOpen: true,
      archId: archId,
      archName: arch.name
    });
  };

  const confirmDeleteArchitecture = async () => {
    const archId = deleteConfirmModal.archId;
    if (!archId) return;

    try {
      console.log('📡 Suppression de l\'architecture via API...', archId);

      await architecturesApi.deleteArchitecture(String(archId));

      console.log('✅ Architecture supprimée');

      // Reload architectures list
      await loadProjectData();
      success('Architecture supprimée avec succès');

    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression:', error);
      const errorMsg = error.response?.data?.error || error.message;
      showError(`Erreur lors de la suppression: ${errorMsg}`);

      // Fallback to localStorage
      const updated = architectures.filter(a => a.id !== archId);
      setArchitectures(updated);
      localStorage.setItem(`project.${id}.architectures`, JSON.stringify(updated));
    }
  };

  const handleDuplicateArchitecture = async (arch: Architecture) => {
    try {
      const newName = `${arch.name} (Copie)`;
      console.log('📡 Duplication de l\'architecture via API...', arch.id, newName);

      await architecturesApi.duplicateArchitecture(String(arch.id), newName);

      console.log('✅ Architecture dupliquée');

      // Reload architectures list
      await loadProjectData();
      alert('✅ Architecture dupliquée avec succès !');

    } catch (error: any) {
      console.error('❌ Erreur lors de la duplication:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors de la duplication: ${errorMsg}`);

      // Fallback to localStorage
      const duplicate: Architecture = {
        ...arch,
        id: Date.now(),
        name: `${arch.name} (Copie)`,
        lastModified: new Date().toISOString(),
        status: 'draft'
      };
      const updated = [...architectures, duplicate];
      setArchitectures(updated);
      localStorage.setItem(`project.${id}.architectures`, JSON.stringify(updated));
    }
  };

  const handleOpenRenameModal = (arch: Architecture) => {
    setRenameModal({
      isOpen: true,
      archId: String(arch.id),
      currentName: arch.name
    });
    setNewName(arch.name);
    setActiveMenu(null);
  };

  const handleRenameArchitecture = async () => {
    if (!renameModal.archId || !newName.trim()) return;

    try {
      console.log('📡 Renommage de l\'architecture via API...', renameModal.archId, newName);

      await architecturesApi.renameArchitecture(renameModal.archId, newName.trim());

      console.log('✅ Architecture renommée');

      // Reload architectures list
      await loadProjectData();
      setRenameModal({ isOpen: false, archId: null, currentName: '' });
      setNewName('');

    } catch (error: any) {
      console.error('❌ Erreur lors du renommage:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du renommage: ${errorMsg}`);
    }
  };

  const handleChangeStatus = async (archId: number, newStatus: Architecture['status']) => {
    try {
      console.log('📡 Changement de statut de l\'architecture via API...', archId, newStatus);

      await architecturesApi.updateArchitectureStatus(String(archId), newStatus);

      console.log('✅ Statut de l\'architecture changé');

      // Reload architectures list
      await loadProjectData();
      setStatusMenu({ isOpen: false, archId: null, currentStatus: 'draft' });
      setActiveMenu(null);

    } catch (error: any) {
      console.error('❌ Erreur lors du changement de statut:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du changement de statut: ${errorMsg}`);
    }
  };

  const handleExportArchitectures = async () => {
    if (selectedArchitectures.size === 0) {
      alert('Veuillez sélectionner au moins une architecture à exporter.');
      return;
    }

    try {
      console.log('📦 Export de', selectedArchitectures.size, 'architecture(s)...');

      const zip = new JSZip();
      const selectedArchitecturesList = architectures.filter(a => selectedArchitectures.has(a.id));

      for (const arch of selectedArchitecturesList) {
        try {
          // Fetch full architecture data from API
          const archData = await architecturesApi.getArchitecture(String(arch.id));

          console.log(`✅ Architecture "${arch.name}" chargée`);

          // Crypter les liens vers ADLS (file_path et file_url) pour la sécurité
          const encryptedArchData = encryptArchitectureBlobs(archData);

          // Create export data (avec diagram_data complet)
          const exportData = {
            type: 'geniac-architecture',
            version: '1.0',
            exportDate: new Date().toISOString(),
            architecture: encryptedArchData
          };

          // Add to zip with sanitized filename
          const filename = `${arch.name.replace(/[^a-z0-9]/gi, '_')}_${arch.id}.json`;
          zip.file(filename, JSON.stringify(exportData, null, 2));

        } catch (error) {
          console.error(`❌ Erreur lors de l'export de l'architecture ${arch.name}:`, error);
          alert(`Erreur lors de l'export de l'architecture "${arch.name}". Continuant avec les autres architectures...`);
        }
      }

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Download zip
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `geniac-architectures-export-${new Date().toISOString().split('T')[0]}.zip`;
      link.click();
      URL.revokeObjectURL(url);

      alert(`✅ ${selectedArchitecturesList.length} architecture(s) exportée(s) avec succès !`);
      deselectAllArchitectures();

    } catch (error: any) {
      console.error('❌ Erreur lors de l\'export:', error);
      alert(`❌ Erreur lors de l'export: ${error.message}`);
    }
  };

  const handleImportArchitectures = () => {
    if (!id) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.zip';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        let architecturesToImport: any[] = [];

        // Handle ZIP files
        if (file.name.endsWith('.zip')) {
          const zip = new JSZip();
          const zipContent = await zip.loadAsync(file);

          // Process each JSON file in the zip
          for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
            if (filename.endsWith('.json') && !zipEntry.dir) {
              const text = await zipEntry.async('text');
              const data = JSON.parse(text);

              // Validate format
              if (data.type !== 'geniac-architecture') {
                alert(`❌ Le fichier "${filename}" n'est pas un export d'architecture GenIaC valide.`);
                continue;
              }

              architecturesToImport.push(data);
            }
          }
        }
        // Handle single JSON files
        else if (file.name.endsWith('.json')) {
          const text = await file.text();
          const data = JSON.parse(text);

          // Validate format
          if (data.type !== 'geniac-architecture') {
            alert('❌ Ce fichier n\'est pas un export d\'architecture GenIaC. Veuillez importer un fichier d\'architecture valide.');
            return;
          }

          architecturesToImport.push(data);
        } else {
          alert('❌ Format de fichier non supporté. Veuillez importer un fichier .json ou .zip');
          return;
        }

        if (architecturesToImport.length === 0) {
          alert('❌ Aucune architecture valide trouvée dans le fichier.');
          return;
        }

        // Import architectures via API
        console.log('📦 Import de', architecturesToImport.length, 'architecture(s)...');
        let successCount = 0;
        let errorCount = 0;

        for (const archData of architecturesToImport) {
          try {
            // Décrypter les liens vers ADLS avant l'import
            const decryptedArch = decryptArchitectureBlobs(archData.architecture);

            await architecturesApi.createArchitecture({
              projectId: id,
              name: `${decryptedArch.name} (Importé)`,
              description: decryptedArch.description || '',
              provider: decryptedArch.provider,
              diagramData: decryptedArch.diagram_data
            });

            console.log(`✅ Architecture "${decryptedArch.name}" importée`);
            successCount++;
          } catch (error: any) {
            console.error(`❌ Erreur lors de l'import de l'architecture:`, error);
            errorCount++;
          }
        }

        // Reload architectures
        await loadProjectData();

        if (successCount > 0) {
          alert(`✅ ${successCount} architecture(s) importée(s) avec succès !${errorCount > 0 ? `\n⚠️ ${errorCount} architecture(s) en erreur.` : ''}`);
        } else {
          alert('❌ Aucune architecture n\'a pu être importée.');
        }

      } catch (error: any) {
        console.error('❌ Erreur lors de l\'importation:', error);
        alert(`❌ Erreur lors de l'importation: ${error.message}`);
      }
    };
    input.click();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 1) return 'À l\'instant';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return date.toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (status: Architecture['status']) => {
    const styles = {
      draft: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      validated: 'bg-blue-100 text-blue-800 border border-blue-300',
      deployed: 'bg-green-100 text-green-800 border border-green-300'
    };
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${styles[status]} inline-flex items-center gap-1.5`}>
        {t(`status.${status}`)}
      </span>
    );
  };

  const filteredArchitectures = architectures
    .filter(arch => {
      const matchesSearch = arch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           arch.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStatus === 'all' || arch.status === filterStatus;
      return matchesSearch && matchesFilter;
    });

  if (!project) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <p className="text-gray-500">Projet non trouvé</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Retour aux projets
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition"
          >
            <FiArrowLeft />
            <span>Retour aux projets</span>
          </button>

          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: project.color }}
              >
                {(project.name || 'PR').substring(0, 2).toUpperCase()}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
                <p className="text-gray-600 mt-1">{project.description}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <BsDiagram3 size={16} />
                    {architectures.length} architecture{architectures.length > 1 ? 's' : ''}
                  </span>
                  <span>•</span>
                  <span>{project.provider}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {selectedArchitectures.size > 0 && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-4 py-2 rounded-lg">
                  <FiCheck className="text-blue-600" size={18} />
                  <span className="text-sm font-medium text-blue-900">
                    {selectedArchitectures.size} architecture{selectedArchitectures.size > 1 ? 's' : ''} sélectionnée{selectedArchitectures.size > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={deselectAllArchitectures}
                    className="ml-2 p-1 hover:bg-blue-100 rounded transition"
                    title="Tout désélectionner"
                  >
                    <FiX className="text-blue-600" size={16} />
                  </button>
                </div>
              )}
              <button
                onClick={handleImportArchitectures}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition"
              >
                <FiUpload size={18} />
                <span className="font-medium">Importer</span>
              </button>
              <button
                onClick={handleExportArchitectures}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition"
                disabled={selectedArchitectures.size === 0}
                title={selectedArchitectures.size === 0 ? "Sélectionnez des architectures à exporter" : "Exporter les architectures sélectionnées"}
              >
                <FiDownload size={18} />
                <span className="font-medium">Exporter{selectedArchitectures.size > 0 ? ` (${selectedArchitectures.size})` : ''}</span>
              </button>
              <button
                onClick={handleCreateArchitecture}
                className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition shadow-md"
              >
                <FiPlus size={20} />
                <span className="font-medium">Nouvelle Architecture</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mb-6 flex gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une architecture..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
          >
            <option value="all">📊 Tous les statuts</option>
            <option value="draft">📝 Brouillon</option>
            <option value="validated">✓ Validé</option>
            <option value="deployed">🚀 Déployé</option>
          </select>

          {filteredArchitectures.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={selectAllArchitectures}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition px-3 py-2 rounded-lg hover:bg-blue-50"
              >
                Tout sélectionner ({filteredArchitectures.length})
              </button>
              {selectedArchitectures.size > 0 && (
                <>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={deselectAllArchitectures}
                    className="text-sm text-gray-600 hover:text-gray-800 font-medium transition px-3 py-2 rounded-lg hover:bg-gray-50"
                  >
                    Tout désélectionner
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Onglets Architectures / Accès */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('architectures')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'architectures'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BsDiagram3Icon size={15} /> Architectures ({architectures.length})
          </button>
          {['owner', 'admin'].includes(user?.role || '') && (
            <button
              onClick={() => setActiveTab('access')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === 'access'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FiShield size={15} /> Accès & Permissions
            </button>
          )}
        </div>

        {/* Panneau Accès */}
        {activeTab === 'access' && id && (
          <ProjectAccessPanel
            projectId={id}
            canManage={['owner', 'admin'].includes(user?.role || '')}
          />
        )}

        {/* Architectures Table */}
        {activeTab === 'architectures' && (filteredArchitectures.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <BsDiagram3 size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchTerm || filterStatus !== 'all'
                ? 'Aucune architecture trouvée'
                : 'Aucune architecture'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterStatus !== 'all'
                ? 'Essayez de modifier vos filtres'
                : 'Créez votre première architecture pour ce projet'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button
                onClick={handleCreateArchitecture}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
              >
                <FiPlus size={20} />
                Créer une architecture
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={filteredArchitectures.length > 0 && selectedArchitectures.size === filteredArchitectures.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllArchitectures();
                        } else {
                          deselectAllArchitectures();
                        }
                      }}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Ressources
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Modifié
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Par
                  </th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredArchitectures.map((arch) => (
                  <tr
                    key={arch.id}
                    className={`hover:bg-gray-50 transition ${
                      selectedArchitectures.has(arch.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedArchitectures.has(arch.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleArchitectureSelection(arch.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 cursor-pointer" onClick={() => handleOpenArchitecture(arch.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <BsDiagram3 className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{arch.name}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{arch.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 cursor-pointer" onClick={() => handleOpenArchitecture(arch.id)}>{arch.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 cursor-pointer" onClick={() => handleOpenArchitecture(arch.id)}>{arch.nodesCount} ressources</td>
                    <td className="px-6 py-4 cursor-pointer" onClick={() => handleOpenArchitecture(arch.id)}>{getStatusBadge(arch.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 cursor-pointer" onClick={() => handleOpenArchitecture(arch.id)}>{formatDate(arch.lastModified)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 cursor-pointer" onClick={() => handleOpenArchitecture(arch.id)}>{arch.createdBy}</td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            if (activeMenu === arch.id) {
                              setActiveMenu(null);
                              setMenuPosition(null);
                            } else {
                              setActiveMenu(arch.id);
                              const MENU_HEIGHT = 420;
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const right = window.innerWidth - rect.right;
                              if (spaceBelow >= MENU_HEIGHT) {
                                setMenuPosition({ top: rect.bottom + 4, right });
                              } else {
                                // Ancrer le bas du menu au dessus du bouton (indépendant de la vraie hauteur)
                                setMenuPosition({ bottom: window.innerHeight - rect.top + 4, right });
                              }
                            }
                          }}
                          className="p-2 hover:bg-gray-100 rounded-full transition"
                        >
                          <FiMoreVertical className="text-gray-600" />
                        </button>

                        {activeMenu === arch.id && menuPosition && (
                          <div
                            className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999]"
                            style={{
                              top: menuPosition.top !== undefined ? `${menuPosition.top}px` : 'auto',
                              bottom: menuPosition.bottom !== undefined ? `${menuPosition.bottom}px` : 'auto',
                              right: `${menuPosition.right}px`
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenArchitecture(arch.id);
                                setActiveMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                            >
                              <FiExternalLink size={16} />
                              Ouvrir
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenRenameModal(arch);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                            >
                              <FiEdit size={16} />
                              Renommer
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateArchitecture(arch);
                                setActiveMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                            >
                              <FiCopy size={16} />
                              Dupliquer
                            </button>
                            <hr className="my-2" />
                            <div className="px-2 py-1">
                              <div className="text-xs font-semibold text-gray-500 px-2 mb-1">Changer le statut</div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeStatus(arch.id, 'draft');
                                }}
                                disabled={arch.status === 'draft'}
                                className={`w-full px-4 py-2 text-left rounded flex items-center gap-3 transition ${
                                  arch.status === 'draft'
                                    ? 'bg-yellow-50 text-yellow-800 cursor-default'
                                    : 'hover:bg-gray-50 text-gray-700'
                                }`}
                              >
                                <span>📝</span>
                                Brouillon
                                {arch.status === 'draft' && <FiCheck size={14} className="ml-auto text-yellow-600" />}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeStatus(arch.id, 'validated');
                                }}
                                disabled={arch.status === 'validated'}
                                className={`w-full px-4 py-2 text-left rounded flex items-center gap-3 transition ${
                                  arch.status === 'validated'
                                    ? 'bg-blue-50 text-blue-800 cursor-default'
                                    : 'hover:bg-gray-50 text-gray-700'
                                }`}
                              >
                                <span>✓</span>
                                Validé
                                {arch.status === 'validated' && <FiCheck size={14} className="ml-auto text-blue-600" />}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeStatus(arch.id, 'deployed');
                                }}
                                disabled={arch.status === 'deployed'}
                                className={`w-full px-4 py-2 text-left rounded flex items-center gap-3 transition ${
                                  arch.status === 'deployed'
                                    ? 'bg-green-50 text-green-800 cursor-default'
                                    : 'hover:bg-gray-50 text-gray-700'
                                }`}
                              >
                                <span>🚀</span>
                                Déployé
                                {arch.status === 'deployed' && <FiCheck size={14} className="ml-auto text-green-600" />}
                              </button>
                            </div>
                            <hr className="my-2" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // TODO: Export functionality
                                setActiveMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                            >
                              <FiDownload size={16} />
                              Exporter
                            </button>
                            <hr className="my-2" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteArchitecture(arch.id);
                                setActiveMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600"
                            >
                              <FiTrash2 size={16} />
                              Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {/* Create Architecture Modal */}
        <CreateArchitectureModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleSaveNewArchitecture}
          projectName={project.name}
          provider={project?.provider?.toLowerCase()}
        />

        {/* Rename Architecture Modal */}
        {renameModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Renommer l'architecture</h2>
              <p className="text-gray-600 text-sm mb-4">
                Nom actuel: <span className="font-medium">{renameModal.currentName}</span>
              </p>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nouveau nom"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-6 text-gray-900 bg-white"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameArchitecture();
                  } else if (e.key === 'Escape') {
                    setRenameModal({ isOpen: false, archId: null, currentName: '' });
                    setNewName('');
                  }
                }}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setRenameModal({ isOpen: false, archId: null, currentName: '' });
                    setNewName('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRenameArchitecture}
                  disabled={!newName.trim() || newName === renameModal.currentName}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Renommer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, archId: null, archName: '' })}
        onConfirm={confirmDeleteArchitecture}
        title="Supprimer l'architecture"
        message={`Êtes-vous sûr de vouloir supprimer l'architecture "${deleteConfirmModal.archName}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
      />
    </DashboardLayout>
  );
};

export default ProjectDetailPage;
